import { Box, Stack, Typography } from "@mui/material";

import React, { useState } from "react";
import CaptionAndFilter from "./component/CaptionAndFilter";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { VITAL_SIGNS_GRAPH } from "utils/reactQueryKeys";
import { useParams } from "react-router-dom";
import { useSanitizeQueryParams } from "utils/sanitizeQuery";
import CustomLoader from "components/atoms/CustomLoader";
import CustomRefLine from "./component/CustomRefLine";
import CustomGraphTooltip from "./component/CustomGraphTooltip";

const Graph = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        key={JSON.stringify(data?.graph ?? [])}
        data={data?.graph ?? []}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="xName" />

        <YAxis
          unit={data?.range?.unit}
          domain={["auto", Number(data?.range?.normal?.max || 0) + 5]}
          allowDecimals
          tickCount={10}
        />
        <Tooltip content={<CustomGraphTooltip yName="Temperature" />} />

        <ReferenceLine
          y={Number(data?.range?.normal?.max ?? 0)}
          label={
            <CustomRefLine
              range={`${data?.range?.normal?.min}${data?.range?.unit} - ${data?.range?.normal?.max}${data?.range?.unit}`}
            />
          }
          stroke="#00843566"
          strokeDasharray="5 5"
        />
        <Line
          type="monotone"
          dataKey="yValue"
          unit={data?.range?.unit}
          stroke="#FF2F00"
          strokeWidth={3}
          dot={false}
        />
        <ReferenceLine
          y={Number(data?.range?.normal?.min ?? 0)}
          label=""
          stroke="#00843566"
          strokeDasharray="5 5"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const VITALS_CHECKED = "temperature";
function Temperature() {
  const { id } = useParams();

  const [filter, setfilter] = useState({
    name: "This Month",
    value: "ThisMonth",
  });

  //get vital signs
  const {
    data: vitalsGraph,
    isLoading,
    isError,
  } = useCustomQuery(
    [
      VITAL_SIGNS_GRAPH,
      id,
      {
        timeType: filter.value,
        vitalSign: VITALS_CHECKED,
      },
    ],
    {
      url: useSanitizeQueryParams(
        `/visit/patients/${id}/vitals-graph?timeType=${filter.value}&vitalSign=${VITALS_CHECKED}`
      ),
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleDropdown = (item) => {
    setfilter(item);
  };
  return (
    <Stack
      spacing={4}
      sx={{
        border: {
          lg: "1px solid rgba(196, 196, 196, 0.25)",
        },
        padding: {
          xs: 0,
          lg: 3,
        },
        borderRadius: {
          xs: "none",
          lg: "12px",
        },
      }}
    >
      <CaptionAndFilter
        filter={filter}
        handleDropdown={handleDropdown}
        icon={
          <svg
            width="16"
            height="29"
            viewBox="0 0 16 29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.6945 4.76042H11.0423C10.7062 4.76042 10.4336 4.48791 10.4336 4.1517C10.4336 3.81548 10.7062 3.54297 11.0423 3.54297H14.6945C15.0307 3.54297 15.3032 3.81548 15.3032 4.1517C15.3032 4.48791 15.0306 4.76042 14.6945 4.76042ZM14.6945 8.41262H11.0423C10.7062 8.41262 10.4336 8.14011 10.4336 7.80389C10.4336 7.46773 10.7062 7.19516 11.0423 7.19516H14.6945C15.0307 7.19516 15.3032 7.46767 15.3032 7.80389C15.3032 8.14011 15.0306 8.41262 14.6945 8.41262ZM14.6945 12.0648H11.0423C10.7062 12.0648 10.4336 11.7923 10.4336 11.4561C10.4336 11.1199 10.7062 10.8474 11.0423 10.8474H14.6945C15.0307 10.8474 15.3032 11.1199 15.3032 11.4561C15.3032 11.7923 15.0306 12.0648 14.6945 12.0648Z"
              fill="#DB1E36"
            />
            <path
              d="M5.56505 24.8479C6.23741 24.8479 6.78245 24.3028 6.78245 23.6305C6.78245 22.9581 6.23741 22.4131 5.56505 22.4131C4.8927 22.4131 4.34766 22.9581 4.34766 23.6305C4.34766 24.3028 4.8927 24.8479 5.56505 24.8479Z"
              fill="#DB1E36"
            />
            <path
              d="M8.28375 19.59C8.20112 19.5343 8.13344 19.4592 8.08664 19.3712C8.03984 19.2832 8.01536 19.185 8.01534 19.0854V2.88798C8.01534 1.57122 6.91607 0.5 5.56491 0.5C4.21374 0.5 3.11452 1.57122 3.11452 2.88798V19.0854C3.11452 19.2878 3.01395 19.4769 2.84612 19.5901C1.49938 20.4983 0.695312 22.0088 0.695312 23.6306C0.695312 26.1717 2.67823 28.3049 5.2096 28.4872L5.2271 28.4884C5.30011 28.4935 5.37323 28.4969 5.4464 28.4987C5.55071 28.5012 5.65507 28.5003 5.75932 28.496C5.86384 28.492 5.9682 28.4847 6.07224 28.4741C8.55812 28.2184 10.4346 26.136 10.4346 23.6306C10.4345 22.0088 9.63048 20.4982 8.28375 19.59ZM5.56491 26.0654C4.22233 26.0654 3.13011 24.9731 3.13011 23.6306C3.13011 22.4982 3.90722 21.5439 4.95623 21.2729V7.80445C4.95623 7.46828 5.22874 7.19572 5.56496 7.19572C5.90113 7.19572 6.17369 7.46823 6.17369 7.80445V21.2729C7.22254 21.5439 7.9997 22.4982 7.9997 23.6306C7.9997 24.9731 6.90743 26.0654 5.56491 26.0654ZM14.6954 15.7174H11.0432C10.7071 15.7174 10.4345 15.4448 10.4345 15.1087C10.4345 14.7725 10.7071 14.4999 11.0432 14.4999H14.6954C15.0316 14.4999 15.3041 14.7725 15.3041 15.1087C15.3041 15.4448 15.0315 15.7174 14.6954 15.7174Z"
              fill="#DB1E36"
            />
          </svg>
        }
        title={"Body temperature"}
        subTitle={
          <>
            {" "}
            Patient's body temperature in{" "}
            {!isLoading && !isError && vitalsGraph?.data ? (
              vitalsGraph?.data?.range?.unit
            ) : (
              <>
                <sup>o</sup>C
              </>
            )}
          </>
        }
      />
      <Stack
        sx={{
          width: {
            xs: "70vw",
            sm: "50vw",
          },
          height: {
            xs: "35vh",
            sm: "40vh",
            md: "70vh",
          },
        }}
      >
        {isLoading ? (
          <CustomLoader />
        ) : !isError ? (
          <Graph data={vitalsGraph.data} />
        ) : (
          "No data to display"
        )}
        {!isLoading && !isError && (
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
              textAlign: "center",
            }}
          >
            {filter.name}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default Temperature;
